import * as THREE from "three";

export interface Animatable {
  group: THREE.Group;
  update(phase: number, active: boolean): void;
}

function box(w: number, h: number, d: number, color: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    new THREE.MeshLambertMaterial({ color })
  );
  return m;
}

// ---------- MARIO ----------

export interface MarioRig extends Animatable {
  setPower(p: "small" | "big" | "fire"): void;
  setCrouch(c: boolean): void;
}

export function makeMario(): MarioRig {
  const group = new THREE.Group();
  const RED = 0xd63b2f;
  const BLUE = 0x2456d6;
  const SKIN = 0xffc8a0;
  const BROWN = 0x5a3212;

  const bodyGroup = new THREE.Group();
  group.add(bodyGroup);

  // small mario proportions (total ~1.0)
  const hat = box(0.52, 0.14, 0.5, RED);
  hat.position.set(0, 0.78, 0);
  const brim = box(0.42, 0.07, 0.22, RED);
  brim.position.set(0.18, 0.74, 0);
  const head = box(0.5, 0.3, 0.48, SKIN);
  head.position.set(0, 0.6, 0);
  const nose = box(0.14, 0.1, 0.14, SKIN);
  nose.position.set(0.26, 0.58, 0);
  const mustache = box(0.2, 0.06, 0.16, BROWN);
  mustache.position.set(0.22, 0.48, 0);
  const torso = box(0.52, 0.34, 0.46, BLUE);
  torso.position.set(0, 0.3, 0);
  const shirt = box(0.54, 0.16, 0.48, RED);
  shirt.position.set(0, 0.4, 0);
  const armL = box(0.14, 0.3, 0.3, RED);
  armL.position.set(0, 0.32, 0.29);
  const armR = box(0.14, 0.3, 0.3, RED);
  armR.position.set(0, 0.32, -0.29);
  const legL = box(0.22, 0.24, 0.32, BLUE);
  legL.position.set(-0.13, 0.1, 0.12);
  const legR = box(0.22, 0.24, 0.32, BLUE);
  legR.position.set(0.13, 0.1, -0.12);
  const shoeL = box(0.26, 0.1, 0.4, BROWN);
  shoeL.position.set(-0.13, -0.02, 0.15);
  const shoeR = box(0.26, 0.1, 0.4, BROWN);
  shoeR.position.set(0.13, -0.02, -0.15);

  bodyGroup.add(hat, brim, head, nose, mustache, torso, shirt, armL, armR, legL, legR, shoeL, shoeR);

  // feet at y = -0.07; group origin at feet center
  group.position.y = 0.07;

  const rig: MarioRig = {
    group,
    setPower(p: "small" | "big" | "fire") {
      const s = p === "small" ? 1 : 1.75;
      bodyGroup.scale.set(s, s, s);
      const shirtMat = (shirt.material as THREE.MeshLambertMaterial).color;
      const hatMat = (hat.material as THREE.MeshLambertMaterial).color;
      if (p === "fire") {
        shirtMat.setHex(0xffffff);
        hatMat.setHex(0xf0f0f0);
        (torso.material as THREE.MeshLambertMaterial).color.setHex(0xffffff);
      } else if (p === "big") {
        shirtMat.setHex(RED);
        hatMat.setHex(RED);
        (torso.material as THREE.MeshLambertMaterial).color.setHex(BLUE);
      } else {
        shirtMat.setHex(RED);
        hatMat.setHex(RED);
        (torso.material as THREE.MeshLambertMaterial).color.setHex(BLUE);
      }
    },
    setCrouch(c: boolean) {
      bodyGroup.scale.x = c ? 1.15 : 1;
      bodyGroup.scale.z = c ? 1.15 : 1;
    },
    update(phase: number, active: boolean) {
      const swing = active ? Math.sin(phase) * 0.5 : 0;
      legL.rotation.x = swing;
      legR.rotation.x = -swing;
      shoeL.rotation.x = -swing;
      shoeR.rotation.x = swing;
      armL.rotation.x = -swing * 0.6;
      armR.rotation.x = swing * 0.6;
      // subtle bob
      bodyGroup.position.y = active ? Math.abs(Math.sin(phase)) * 0.05 : 0;
    }
  };
  return rig;
}

// ---------- GOOMBA ----------

export function makeGoomba(): Animatable {
  const group = new THREE.Group();
  const BROWN = 0x8a4b25;
  const DARK = 0x4a2410;
  const body = box(0.72, 0.6, 0.6, BROWN);
  body.position.y = 0.35;
  const footL = box(0.28, 0.16, 0.4, DARK);
  footL.position.set(-0.18, 0.08, 0);
  const footR = box(0.28, 0.16, 0.4, DARK);
  footR.position.set(0.18, 0.08, 0);
  const eyeL = box(0.12, 0.18, 0.05, 0xffffff);
  eyeL.position.set(-0.14, 0.48, 0.31);
  const eyeR = box(0.12, 0.18, 0.05, 0xffffff);
  eyeR.position.set(0.14, 0.48, 0.31);
  const pupilL = box(0.05, 0.1, 0.03, 0x111111);
  pupilL.position.set(-0.14, 0.45, 0.34);
  const pupilR = box(0.05, 0.1, 0.03, 0x111111);
  pupilR.position.set(0.14, 0.45, 0.34);
  group.add(body, footL, footR, eyeL, eyeR, pupilL, pupilR);

  return {
    group,
    update(phase: number, active: boolean) {
      const s = active ? Math.sin(phase * 8) * 0.3 : 0;
      footL.rotation.x = s;
      footR.rotation.x = -s;
      group.scale.y = active ? 1 - Math.abs(Math.sin(phase * 8)) * 0.06 : 1;
    }
  };
}

// ---------- KOOPA ----------

export function makeKoopa(shell: boolean): Animatable {
  const group = new THREE.Group();
  const GREEN = 0x2eb83a;
  const shellMesh = box(0.7, 0.55, 0.6, GREEN);
  shellMesh.position.y = 0.32;
  const belly = box(0.62, 0.4, 0.2, 0xf0e0a0);
  belly.position.set(0, 0.28, 0.22);
  let neck: THREE.Mesh | null = null;
  let head: THREE.Mesh | null = null;
  let footL: THREE.Mesh | null = null;
  let footR: THREE.Mesh | null = null;
  if (!shell) {
    neck = box(0.2, 0.35, 0.2, 0xf8d870);
    neck.position.set(0.28, 0.7, 0);
    head = box(0.34, 0.28, 0.3, 0xf8d870);
    head.position.set(0.38, 0.9, 0);
    footL = box(0.2, 0.14, 0.34, 0xf8d870);
    footL.position.set(-0.12, 0.07, 0.14);
    footR = box(0.2, 0.14, 0.34, 0xf8d870);
    footR.position.set(0.12, 0.07, -0.14);
    group.add(neck, head, footL, footR);
  }
  group.add(shellMesh, belly);

  return {
    group,
    update(phase: number, active: boolean) {
      if (!active) return;
      const s = Math.sin(phase * 8) * 0.3;
      if (footL) footL.rotation.x = s;
      if (footR) footR.rotation.x = -s;
      if (head) head.position.y = 0.9 + Math.sin(phase * 8) * 0.03;
    }
  };
}

// ---------- PARATROOPA ----------

export function makeParatroopa(): Animatable {
  const group = new THREE.Group();
  const GREEN = 0x2eb83a;
  const WHITE = 0xf0f0f0;
  const DARK = 0x4a2410;
  // shell
  const shellMesh = box(0.7, 0.55, 0.6, GREEN);
  shellMesh.position.y = 0.32;
  // belly
  const belly = box(0.62, 0.4, 0.2, 0xf0e0a0);
  belly.position.set(0, 0.28, 0.22);
  // neck + head
  const neck = box(0.2, 0.35, 0.2, 0xf8d870);
  neck.position.set(0.28, 0.7, 0);
  const head = box(0.34, 0.28, 0.3, 0xf8d870);
  head.position.set(0.38, 0.9, 0);
  const eye = box(0.06, 0.06, 0.03, DARK);
  eye.position.set(0.52, 0.92, 0.16);
  // feet
  const footL = box(0.2, 0.14, 0.34, 0xf8d870);
  footL.position.set(-0.12, 0.07, 0.14);
  const footR = box(0.2, 0.14, 0.34, 0xf8d870);
  footR.position.set(0.12, 0.07, -0.14);
  // wings
  const wingL = box(0.15, 0.35, 0.5, WHITE);
  wingL.position.set(-0.05, 0.65, 0);
  const wingR = box(0.15, 0.35, 0.5, WHITE);
  wingR.position.set(0.05, 0.65, 0);
  group.add(shellMesh, belly, neck, head, eye, footL, footR, wingL, wingR);

  return {
    group,
    update(phase: number, active: boolean) {
      const s = active ? Math.sin(phase * 8) * 0.3 : 0;
      footL.rotation.x = s;
      footR.rotation.x = -s;
      if (active) {
        // wing flap: rotate up and down
        wingL.rotation.x = Math.sin(phase * 8) * 0.6;
        wingR.rotation.x = Math.sin(phase * 8) * 0.6;
      } else {
        wingL.rotation.x = 0;
        wingR.rotation.x = 0;
      }
    }
  };
}

// ---------- MUSHROOM ----------

export function makeMushroom(): Animatable {
  const group = new THREE.Group();
  const cap = box(0.66, 0.32, 0.66, 0xe03020);
  cap.position.y = 0.5;
  const spot1 = box(0.14, 0.03, 0.14, 0xffffff);
  spot1.position.set(0.14, 0.67, 0.14);
  const spot2 = box(0.14, 0.03, 0.14, 0xffffff);
  spot2.position.set(-0.14, 0.67, -0.14);
  const spot3 = box(0.14, 0.03, 0.14, 0xffffff);
  spot3.position.set(0, 0.67, 0);
  const stem = box(0.4, 0.34, 0.4, 0xf0d890);
  stem.position.y = 0.22;
  group.add(cap, stem, spot1, spot2, spot3);
  return { group, update: () => undefined };
}

// ---------- CASTLE ----------

export function makeCastle(): THREE.Group {
  const g = new THREE.Group();
  const GRAY = 0xa8a8a8;
  const base = box(8, 6, 4, GRAY);
  base.position.y = 3;
  const tower = box(3, 4, 4, GRAY);
  tower.position.set(0, 8, 0);
  const roof = new THREE.Mesh(
    new THREE.ConeGeometry(2.4, 2.2, 4),
    new THREE.MeshLambertMaterial({ color: 0x606060 })
  );
  roof.position.set(0, 11, 0);
  roof.rotation.y = Math.PI / 4;
  const door = box(1.6, 2.6, 0.3, 0x2a2a2a);
  door.position.set(0, 1.3, 2.01);
  // crenellations
  for (let i = -3; i <= 3; i += 2) {
    const b = box(0.8, 0.6, 0.8, GRAY);
    b.position.set(i, 6.3, 1.6);
    g.add(b);
  }
  g.add(base, tower, roof, door);
  return g;
}

// ---------- FLAG ----------

export interface FlagRig {
  group: THREE.Group;
  flag: THREE.Mesh;
  setFlagY(y: number): void;
}

export function makeFlag(x: number, baseY: number): FlagRig {
  const g = new THREE.Group();
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.08, 18, 12),
    new THREE.MeshLambertMaterial({ color: 0x58b858 })
  );
  pole.position.set(x, baseY + 9, 0);
  const ball = new THREE.Mesh(
    new THREE.SphereGeometry(0.35, 12, 12),
    new THREE.MeshLambertMaterial({ color: 0x58b858 })
  );
  ball.position.set(x, baseY + 18, 0);
  const flag = new THREE.Mesh(
    new THREE.BoxGeometry(1.4, 1.0, 0.08),
    new THREE.MeshLambertMaterial({ color: 0x2eb83a })
  );
  flag.position.set(x - 0.75, baseY + 16, 0);
  g.add(pole, ball, flag);
  return {
    group: g,
    flag,
    setFlagY(y: number) {
      flag.position.y = y;
    }
  };
}

// ---------- TILE COLORS ----------

// ---------- FLOWER ----------

export function makeFlower(): THREE.Group {
  const g = new THREE.Group();
  const STEEM = 0x30a030;
  const PETAL = 0xf03020;
  const CENTER = 0xf0d020;
  const stem = box(0.12, 0.25, 0.12, STEEM);
  stem.position.y = 0.125;
  const petal1 = box(0.18, 0.06, 0.18, PETAL);
  petal1.position.y = 0.28;
  const petal2 = box(0.12, 0.04, 0.12, CENTER);
  petal2.position.y = 0.3;
  g.add(stem, petal1, petal2);
  return g;
}

// ---------- TILE COLORS ----------

export function tileColor(type: number): number {
  switch (type) {
    case 1: return 0xd08048; // ground
    case 2: return 0xb8482a; // brick
    case 3: return 0xf0a828; // question
    case 4: return 0x8a6a40; // used
    case 5: return 0x30b840; // pipe
    case 6: return 0xc87838; // stair block
    default: return 0xff00ff;
  }
}
